import { z } from 'zod';
import { amountInputSchema, amountSchema } from '../common/amount.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { isoDateTimeSchema, localeSchema } from '../common/primitives.js';
import { branchRefSchema, dateOnlySchema, employeeRefSchema } from '../common/refs.js';
import { captureTaskViewSchema } from '../tablets/contract.js';
import { tierQuoteSchema } from '../tiers/contract.js';
import { transferCommissionModeSchema, transferDirectionSchema } from './direction.js';
import { editPartyInputSchema, partyInputSchema, partySyncStatusSchema, receiverSnapshotSchema, senderSnapshotSchema, } from './parties.js';
/**
 * Transfers (transfers.md sections 4 and 7).
 *
 * DRAFT (مبدئية): no money effect. POSTED (مسجلة): counted in balances and
 * reports. CANCELLED (ملغاة): a discarded draft, or a posted transfer whose
 * effect was reversed once.
 */
export const TRANSFER_STATES = ['DRAFT', 'POSTED', 'CANCELLED'];
export const transferStateSchema = z.enum(TRANSFER_STATES);
export const transferReferenceSchema = z.string().trim().min(1).max(64);
export const notesSchema = z.string().trim().max(1000);
// ---------------------------------------------------------------------------
// Drafts — حوالة جديدة
// ---------------------------------------------------------------------------
/**
 * POST /transfers/drafts — the branch is always the session branch. The box
 * must be an active box of that branch and the account an active account
 * allowed in it (TRF-16). No commission field: it always comes from the tiers.
 */
export const createDraftSchema = z.object({
    /** Omitted: BANK_TO_CASH, the original behaviour (TRF-41). */
    direction: transferDirectionSchema.optional(),
    /**
     * طريقة احتساب العمولة (TRF-60), CASH_TO_BANK only. Omitted: DEDUCTED. Any
     * value sent with BANK_TO_CASH is stored as DEDUCTED.
     */
    commissionMode: transferCommissionModeSchema.optional(),
    boxId: z.string().min(1),
    bankAccountId: z.string().min(1),
    amount: amountInputSchema,
    transferReference: transferReferenceSchema.nullable().optional(),
    senderIsReceiver: z.boolean(),
    /**
     * العميل غير موجود (KAN-33, TRF-69). Omitted: false. True saves the draft
     * without sending anything to the tablet; it is posted without parties or a
     * signature. المرسل هو المستلم is stored as chosen and ignored while set.
     * On an update, true cancels an open capture task and false lets the draft
     * be sent again.
     */
    customerNotPresent: z.boolean().optional(),
    notes: notesSchema.nullable().optional(),
});
/**
 * PATCH /transfers/drafts/:id — changing the amount, box, account or
 * المرسل هو المستلم after the customer signed voids the signature and the form
 * must be sent to the tablet again (TRF-12).
 */
export const updateDraftSchema = createDraftSchema.partial();
/**
 * PATCH /transfers/drafts/:id/parties — the employee corrects party data the
 * customer entered, before posting. Any change voids the signature (TRF-12).
 * The present party (`presentPartyRole`) needs its document (TRF-44).
 */
export const updateDraftPartiesSchema = z.object({
    receiver: partyInputSchema,
    sender: partyInputSchema.nullable(),
});
/** POST /transfers/drafts/:id/send — إرسال إلى التابلت or هذا الجهاز. */
export { captureTargetSchema as sendDraftSchema } from '../tablets/contract.js';
/** Another non-cancelled transfer with the same reference on the same account (TRF-15). */
export const duplicateReferenceSchema = z.object({
    duplicate: z.boolean(),
    transfers: z.array(z.object({ id: z.string(), number: z.number().int().nullable() })),
});
/** GET /transfers/duplicate-reference?bankAccountId=&reference=&excludeId= */
export const duplicateReferenceQuerySchema = z.object({
    bankAccountId: z.string().min(1),
    reference: transferReferenceSchema,
    excludeId: z.string().optional(),
});
/** Why the draft cannot be posted yet; empty when it can. */
export const POSTING_BLOCKERS = [
    'NO_TIER',
    'COMMISSION_NOT_BELOW_AMOUNT',
    'INSUFFICIENT_BOX_BALANCE',
    /** CASH_TO_BANK: the bank account cannot send the net (TRF-42). */
    'INSUFFICIENT_BANK_BALANCE',
    'BOX_INACTIVE',
    'BANK_ACCOUNT_INACTIVE',
    'CAPTURE_MISSING',
    'SIGNATURE_VOID',
];
export const postingBlockerSchema = z.enum(POSTING_BLOCKERS);
export const draftViewSchema = z.object({
    id: z.string(),
    state: transferStateSchema,
    direction: transferDirectionSchema,
    commissionMode: transferCommissionModeSchema,
    branch: branchRefSchema,
    box: z.object({ id: z.string(), name: z.string(), balance: amountSchema }),
    bankAccount: z.object({
        id: z.string(),
        bankName: z.string(),
        accountName: z.string(),
        balance: amountSchema,
    }),
    /** The entered amount (the tier is looked up on it). */
    amount: amountSchema,
    /**
     * Live from the current active tiers; stored only at posting. With ADDED its
     * `amount` is the cash to receive (entered + commission) and its `net` the
     * entered amount (TRF-60).
     */
    quote: tierQuoteSchema,
    transferReference: z.string().nullable(),
    duplicateReference: duplicateReferenceSchema,
    senderIsReceiver: z.boolean(),
    /** العميل غير موجود: no capture is needed or accepted (TRF-69, TRF-70). */
    customerNotPresent: z.boolean(),
    notes: z.string().nullable(),
    /** The latest capture task, if the draft was sent. */
    capture: captureTaskViewSchema.nullable(),
    /** True when the customer signed and nothing signed changed since. */
    signatureValid: z.boolean(),
    blockers: z.array(postingBlockerSchema),
    /**
     * تأكيد رقم الهاتف (transfers.md section 10): with the setting on, the
     * phone of the party present (the one who signs) is verified before
     * posting. `required` is false when the setting is off, the customer is not
     * present, nothing is signed yet, or Customers already holds the phone as
     * verified; `phone` is the present party's signed phone.
     */
    phoneVerification: z.object({ required: z.boolean(), phone: z.string().nullable() }),
    createdBy: employeeRefSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/**
 * POST /transfers/drafts/:id/post — اعتماد الحوالة, with an Idempotency-Key
 * header. Posts atomically: sequential number, balances, tier snapshot. A
 * repeated request returns the first result (TRF-09). With تأكيد رقم الهاتف
 * on, `phoneVerificationId` names the verified code of the party present
 * (transfers.md section 10); the key is bound to the whole body.
 */
export const postDraftSchema = z
    .object({ phoneVerificationId: z.string().trim().min(1).max(64).optional() })
    .strict();
// ---------------------------------------------------------------------------
// Messages, prints, history, signatures
// ---------------------------------------------------------------------------
/**
 * BANK_TO_CASH: `transfer_paid_recipient` to the receiver, `transfer_paid_sender`
 * as the optional copy. CASH_TO_BANK: `transfer_sent_sender` to the sender who
 * handed over the cash, `transfer_sent_recipient` as the optional copy to the
 * receiver (TRF-48).
 */
export const MESSAGE_TEMPLATES = [
    'transfer_paid_recipient',
    'transfer_paid_sender',
    'transfer_sent_sender',
    'transfer_sent_recipient',
];
export const messageTemplateSchema = z.enum(MESSAGE_TEMPLATES);
/**
 * PENDING (بانتظار الإرسال), SENT (تم الإرسال), DELIVERED (تم التسليم, only
 * when the WhatsApp service reports delivery or read), FAILED (فشل الإرسال).
 */
export const MESSAGE_STATUSES = ['PENDING', 'SENT', 'DELIVERED', 'FAILED'];
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);
export const messageViewSchema = z.object({
    id: z.string(),
    template: messageTemplateSchema,
    phone: z.string(),
    language: localeSchema,
    /** `n` of the idempotency key TRANSFERS:{template}:{transferId}:{n}. */
    sequence: z.number().int().positive(),
    status: messageStatusSchema,
    failureReason: z.string().nullable(),
    attempts: z.number().int().nonnegative(),
    createdAt: isoDateTimeSchema,
    sentAt: isoDateTimeSchema.nullable(),
    deliveredAt: isoDateTimeSchema.nullable(),
    failedAt: isoDateTimeSchema.nullable(),
});
/** POST /transfers/:id/messages/resend — sends the current values with a new key. */
export const resendMessagesSchema = z.object({
    template: messageTemplateSchema.optional(),
});
export const printLogViewSchema = z.object({
    id: z.string(),
    reprint: z.boolean(),
    printedBy: employeeRefSchema,
    printedAt: isoDateTimeSchema,
});
/** POST /transfers/:id/prints — records a print (first print or إعادة طباعة). */
export const recordPrintSchema = z.object({ reprint: z.boolean() });
export const signatureViewSchema = z.object({
    id: z.string(),
    signerName: z.string(),
    /** The party who signed: the receiver, or the sender of a CASH_TO_BANK transfer. */
    role: z.enum(['RECEIVER', 'SENDER']),
    signedAt: isoDateTimeSchema,
    /** Paired tablet name; null for هذا الجهاز. */
    tabletName: z.string().nullable(),
    language: localeSchema,
    /** The declaration text exactly as shown. */
    textShown: z.string(),
    /** وُقّع على نسخة سابقة: the transfer was edited after this signature. */
    superseded: z.boolean(),
    /** GET /files/signatures/:id (authenticated). */
    fileUrl: z.string(),
});
/** Field names in سجل التعديلات. */
export const EDITABLE_FIELDS = [
    'amount',
    'commission',
    'net',
    'tier',
    'box',
    'bankAccount',
    'transferReference',
    'senderIsReceiver',
    'receiver.fullName',
    'receiver.phone',
    'receiver.documentType',
    'receiver.documentNumber',
    'receiver.documentCountry',
    'sender.fullName',
    'sender.phone',
    'sender.documentType',
    'sender.documentNumber',
    'sender.documentCountry',
    'notes',
];
export const editableFieldSchema = z.enum(EDITABLE_FIELDS);
export const fieldChangeSchema = z.object({
    field: editableFieldSchema,
    oldValue: z.string().nullable(),
    newValue: z.string().nullable(),
});
export const transferEditViewSchema = z.object({
    id: z.string(),
    /** 1 for the first edit; the Customers reference suffix is -E{sequence}. */
    sequence: z.number().int().positive(),
    reason: z.string(),
    editedBy: employeeRefSchema,
    editedAt: isoDateTimeSchema,
    changes: z.array(fieldChangeSchema),
});
// ---------------------------------------------------------------------------
// Transfer details — تفاصيل الحوالة
// ---------------------------------------------------------------------------
export const tierSnapshotSchema = z.object({
    name: z.string(),
    lowerLimit: amountSchema,
    upperLimit: amountSchema.nullable(),
});
export const transferDetailsSchema = z.object({
    id: z.string(),
    /** Sequential per system, set at posting; null for a draft or discarded draft. */
    number: z.number().int().positive().nullable(),
    state: transferStateSchema,
    direction: transferDirectionSchema,
    commissionMode: transferCommissionModeSchema,
    /** For CANCELLED: whether a draft was discarded or a posted transfer cancelled. */
    cancelledFrom: z.enum(['DRAFT', 'POSTED']).nullable(),
    notes: z.string().nullable(),
    language: localeSchema.nullable(),
    branch: branchRefSchema,
    box: z.object({ id: z.string(), name: z.string(), code: z.string() }),
    bankAccount: z.object({
        id: z.string(),
        bankName: z.string(),
        accountName: z.string(),
        accountNumber: z.string().nullable(),
        iban: z.string().nullable(),
    }),
    /**
     * Posted: what comes in (CASH_TO_BANK: the cash received from the customer,
     * which is entered + commission when ADDED). Draft: the entered amount.
     */
    amount: amountSchema,
    /** Snapshot stored at posting (and on an edit that changed the amount). */
    tier: tierSnapshotSchema.nullable(),
    commission: amountSchema.nullable(),
    net: amountSchema.nullable(),
    transferReference: z.string().nullable(),
    /** Stored at posting: the reference was already used on this account. */
    duplicateReference: z.boolean(),
    senderIsReceiver: z.boolean(),
    /** العميل غير موجود: shown in place of the parties and the signature (TRF-69). */
    customerNotPresent: z.boolean(),
    receiver: receiverSnapshotSchema.nullable(),
    sender: senderSnapshotSchema.nullable(),
    /** بانتظار المزامنة until the Customers outbox succeeds. */
    syncStatus: partySyncStatusSchema.nullable(),
    signatures: z.array(signatureViewSchema),
    messages: z.array(messageViewSchema),
    prints: z.array(printLogViewSchema),
    edits: z.array(transferEditViewSchema),
    createdBy: employeeRefSchema,
    createdAt: isoDateTimeSchema,
    postedBy: employeeRefSchema.nullable(),
    postedAt: isoDateTimeSchema.nullable(),
    cancelledBy: employeeRefSchema.nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancelReason: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// List — الحوالات
// ---------------------------------------------------------------------------
/**
 * GET /transfers — search: number, transfer reference, customer name or
 * phone. Dates are calendar dates in Asia/Amman on the posting date (the
 * creation date for drafts). Data is limited to the user's branches.
 */
export const transferListQuerySchema = paginationQuerySchema.extend({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    branchId: z.string().optional(),
    employeeId: z.string().optional(),
    boxId: z.string().optional(),
    bankAccountId: z.string().optional(),
    state: transferStateSchema.optional(),
    direction: transferDirectionSchema.optional(),
});
export const transferListItemSchema = z.object({
    id: z.string(),
    number: z.number().int().positive().nullable(),
    direction: transferDirectionSchema,
    commissionMode: transferCommissionModeSchema,
    date: isoDateTimeSchema,
    branch: branchRefSchema,
    senderName: z.string().nullable(),
    receiverName: z.string().nullable(),
    amount: amountSchema,
    commission: amountSchema.nullable(),
    net: amountSchema.nullable(),
    bankAccount: z.object({ id: z.string(), bankName: z.string(), accountName: z.string() }),
    box: z.object({ id: z.string(), name: z.string() }),
    employee: employeeRefSchema,
    state: transferStateSchema,
    syncStatus: partySyncStatusSchema.nullable(),
    duplicateReference: z.boolean(),
    /** العميل غير موجود: the row shows the label in place of the party names (TRF-69). */
    customerNotPresent: z.boolean(),
});
export const moneyTotalsSchema = z.object({
    count: z.number().int().nonnegative(),
    amount: amountSchema,
    commission: amountSchema,
    net: amountSchema,
});
/** GET /transfers/totals — the list footer: posted transfers in the same filter only. */
export const transferTotalsSchema = moneyTotalsSchema;
/** Exports: `?format=xlsx|pdf` plus the list or report filters. */
export const EXPORT_FORMATS = ['xlsx', 'pdf'];
export const exportFormatSchema = z.enum(EXPORT_FORMATS);
export const transferExportQuerySchema = transferListQuerySchema
    .omit({ page: true, pageSize: true })
    .extend({ format: exportFormatSchema, locale: localeSchema.default('ar') });
// ---------------------------------------------------------------------------
// Edit and cancel (sections 7.4 and 7.5)
// ---------------------------------------------------------------------------
/**
 * PATCH /transfers/:id (TRANSFER_UPDATE, Idempotency-Key) and
 * POST /transfers/:id/edit-preview. Omitted fields keep their value. The
 * branch is not editable. A changed amount takes its commission from the
 * current active tiers; an unchanged amount keeps its stored commission.
 */
export const editTransferSchema = z.object({
    reason: z.string().trim().min(1).max(500),
    /**
     * The entered amount the tier is looked up on: the stored amount, or the
     * amount transferred (`net`) of an ADDED transfer (TRF-63). The commission
     * mode, like the direction, is not editable.
     */
    amount: amountInputSchema.optional(),
    boxId: z.string().min(1).optional(),
    bankAccountId: z.string().min(1).optional(),
    transferReference: transferReferenceSchema.nullable().optional(),
    senderIsReceiver: z.boolean().optional(),
    /** The present party (`presentPartyRole`) needs its document (TRF-44). */
    receiver: editPartyInputSchema.optional(),
    /** Required when the result is not sender-is-receiver and no sender is stored. */
    sender: editPartyInputSchema.nullable().optional(),
    notes: notesSchema.nullable().optional(),
    /**
     * The verified code of the party present when the edit changes that
     * party's phone and تأكيد رقم الهاتف is on (transfers.md section 10).
     */
    phoneVerificationId: z.string().trim().min(1).max(64).optional(),
});
export const balanceEffectSchema = z.object({
    holder: z.object({ kind: z.enum(['BOX', 'BANK_ACCOUNT']), id: z.string(), name: z.string() }),
    /** Signed change applied by the edit. */
    delta: amountSchema,
    balanceBefore: amountSchema,
    balanceAfter: amountSchema,
});
/** The confirmation dialog: every change and its money effect. */
export const editPreviewSchema = z.object({
    changes: z.array(fieldChangeSchema),
    effects: z.array(balanceEffectSchema),
    commission: z.object({ before: amountSchema, after: amountSchema }),
    net: z.object({ before: amountSchema, after: amountSchema }),
    tier: z.object({ before: tierSnapshotSchema, after: tierSnapshotSchema }),
    /** The receiver or amount changed: the earlier signature becomes وُقّع على نسخة سابقة. */
    supersedesSignature: z.boolean(),
    /** Refusals that saving would raise, e.g. INSUFFICIENT_BALANCE or NO_TIER_FOR_AMOUNT. */
    blockers: z.array(z.string()),
    duplicateReference: duplicateReferenceSchema,
    /**
     * The edit changes the phone of the party present while تأكيد رقم الهاتف
     * is on: the dialog تأكيد رقم الهاتف opens before the edit is saved
     * (transfers.md section 10). `phone` is the new phone in E.164.
     */
    phoneVerification: z.object({ required: z.boolean(), phone: z.string().nullable() }),
});
/** POST /transfers/:id/cancel (TRANSFER_CANCEL, Idempotency-Key). */
export const cancelTransferSchema = z.object({ reason: z.string().trim().min(1).max(500) });
// ---------------------------------------------------------------------------
// Receipt — إيصال الحوالة (section 4.4)
// ---------------------------------------------------------------------------
/**
 * GET /transfers/:id/receipt — everything the 80 mm receipt prints, in the
 * language chosen on the tablet. No document number (TRF-26).
 */
export const receiptSchema = z.object({
    language: localeSchema,
    branchName: z.string(),
    title: z.string(),
    direction: transferDirectionSchema,
    commissionMode: transferCommissionModeSchema,
    number: z.number().int().positive(),
    /** Code 128 payload: the transfer number. */
    barcode: z.string(),
    postedAt: isoDateTimeSchema,
    amount: amountSchema,
    commission: amountSchema,
    net: amountSchema,
    /** العميل غير موجود in the receipt language when posted without the customer (TRF-69). */
    senderName: z.string(),
    receiverName: z.string(),
    bankName: z.string(),
    employeeName: z.string(),
    footer: z.string(),
    currency: z.literal('JOD'),
});
//# sourceMappingURL=contract.js.map